import React, { useState, useEffect, useCallback } from 'react';
import {
  updateUserSystemRole,
  createProgrammeRole,
  createProjectRole,
  getProjectRoles,
  getProgrammeRoles,
  updateProjectRole,
  updateProgrammeRole,
  deleteProjectRole,
  deleteProgrammeRole,
} from '../api/roles';
import { getProgrammes } from '../api/programmes';
import { getProjects } from '../api/projects';
import { Programme } from '../types/programme';
import { Project } from '../types/project';
import { ProjectRole, ProgrammeRole, AssignableSystemRole } from '../types/role';
import Button from './Button';

interface RoleAssignmentModalProps {
  userId: string;
  userName: string;
  currentSystemRole: string;
  /** Called after any successful change so the parent can refresh; keeps the modal open. */
  onSuccess: () => void;
  onClose: () => void;
}

type GrantLevel = 'system' | 'programme' | 'project';
type AccessLevel = 'Editor' | 'Viewer';

const SYSTEM_ROLE_OPTIONS: { value: AssignableSystemRole; description: string }[] = [
  { value: 'Admin', description: 'Full system access, including user management, settings and audit logs' },
  { value: 'Portfolio Manager', description: 'Create/manage all programmes and projects; no back-office admin powers' },
  { value: 'Member', description: 'Create projects/tasks; edit only where granted access' },
  { value: 'Viewer', description: 'Read-only access across the system' },
];

const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  userId,
  userName,
  currentSystemRole,
  onSuccess,
  onClose,
}) => {
  const [grantLevel, setGrantLevel] = useState<GrantLevel>('system');

  // System role
  const [systemRole, setSystemRole] = useState<string>(currentSystemRole);

  // Grant-access selections
  const [programmeIds, setProgrammeIds] = useState<string[]>([]);
  const [programmeRole, setProgrammeRole] = useState<AccessLevel>('Editor');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [projectRole, setProjectRole] = useState<AccessLevel>('Editor');

  // Reference data
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // The user's current access grants (source of truth for the "Current Access" panel)
  const [userProjectRoles, setUserProjectRoles] = useState<ProjectRole[]>([]);
  const [userProgrammeRoles, setUserProgrammeRoles] = useState<ProgrammeRole[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingProjectIds = userProjectRoles.map((r) => String(r.project_id));
  const existingProgrammeIds = userProgrammeRoles.map((r) => String(r.programme_id));

  const programmeName = (id: string) => programmes.find((p) => String(p.id) === id)?.name || 'Unknown';
  const projectName = (id: string) => projects.find((p) => String(p.id) === id)?.name || 'Unknown';

  const reloadUserRoles = useCallback(async () => {
    try {
      const [projectRoles, programmeRoles] = await Promise.all([
        getProjectRoles(userId),
        getProgrammeRoles(userId),
      ]);
      setUserProjectRoles(projectRoles);
      setUserProgrammeRoles(programmeRoles);
    } catch (err) {
      console.error('Failed to load user access roles:', err);
    }
  }, [userId]);

  useEffect(() => {
    getProgrammes().then(setProgrammes).catch((err) => console.error('Failed to load programmes:', err));
    getProjects().then(setProjects).catch((err) => console.error('Failed to load projects:', err));
    reloadUserRoles();
  }, [reloadUserRoles]);

  const toggleId = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  // --- Grant (create) new access -------------------------------------------

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (grantLevel === 'system') {
        if (currentSystemRole === 'Admin' && systemRole !== 'Admin') {
          if (!window.confirm('You are removing Admin privileges from this user. Make sure another admin exists.')) {
            setLoading(false);
            return;
          }
        }
        await updateUserSystemRole(userId, { system_role: systemRole as AssignableSystemRole });
        onSuccess();
      } else if (grantLevel === 'programme') {
        const newIds = programmeIds.filter((id) => !existingProgrammeIds.includes(id));
        if (newIds.length === 0) {
          setError('Select at least one programme the user does not already have a role in.');
          setLoading(false);
          return;
        }
        const results = await Promise.allSettled(
          newIds.map((programmeId) =>
            createProgrammeRole({ programme_id: programmeId, user_id: userId, role: programmeRole })
          )
        );
        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length === results.length) {
          setError(`Failed to assign programme roles:\n${failures.map((f) => (f as PromiseRejectedResult).reason?.message || f).join('\n')}`);
          setLoading(false);
          return;
        }
        setProgrammeIds([]);
        await reloadUserRoles();
        onSuccess();
      } else if (grantLevel === 'project') {
        const newIds = projectIds.filter((id) => !existingProjectIds.includes(id));
        if (newIds.length === 0) {
          setError('Select at least one project the user does not already have a role in.');
          setLoading(false);
          return;
        }
        const results = await Promise.allSettled(
          newIds.map((projectId) =>
            createProjectRole({ project_id: projectId, user_id: userId, role: projectRole })
          )
        );
        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length === results.length) {
          setError(`Failed to assign project roles:\n${failures.map((f) => (f as PromiseRejectedResult).reason?.message || f).join('\n')}`);
          setLoading(false);
          return;
        }
        setProjectIds([]);
        await reloadUserRoles();
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setLoading(false);
    }
  };

  // --- Edit / remove existing access ---------------------------------------

  const runAccessAction = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      await reloadUserRoles();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update access');
    }
  };

  const changeProjectLevel = (roleId: string, role: AccessLevel) =>
    runAccessAction(() => updateProjectRole(roleId, { role }));
  const removeProjectRole = (roleId: string) =>
    runAccessAction(() => deleteProjectRole(roleId));
  const changeProgrammeLevel = (roleId: string, role: AccessLevel) =>
    runAccessAction(() => updateProgrammeRole(roleId, { role }));
  const removeProgrammeRole = (roleId: string) =>
    runAccessAction(() => deleteProgrammeRole(roleId));

  const selectClasses =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';

  const hasCurrentAccess = userProgrammeRoles.length > 0 || userProjectRoles.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Manage Access — {userName}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Set the system role, grant programme/project access, and review current access — all in one place.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error rounded-lg whitespace-pre-line">
            {error}
          </div>
        )}

        {/* ---- Current Access ---- */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Current Access</h4>
          <div className="text-sm mb-3">
            <span className="text-gray-500 dark:text-gray-400">System role: </span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{currentSystemRole}</span>
          </div>
          {!hasCurrentAccess ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No programme or project grants. {currentSystemRole === 'Admin' || currentSystemRole === 'Portfolio Manager'
                ? 'This user already has portfolio-wide access via their system role.'
                : 'Grant access below.'}
            </p>
          ) : (
            <div className="space-y-2">
              {userProgrammeRoles.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm">
                  <span className="inline-flex px-2 py-0.5 rounded bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200 text-xs">Programme</span>
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200" title={programmeName(String(r.programme_id))}>
                    {programmeName(String(r.programme_id))}
                  </span>
                  <select
                    value={r.role}
                    onChange={(e) => changeProgrammeLevel(r.id, e.target.value as AccessLevel)}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Editor">Editor</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeProgrammeRole(r.id)}
                    className="text-error hover:underline text-xs"
                  >
                    Remove
                  </button>
                </div>
              ))}
              {userProjectRoles.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm">
                  <span className="inline-flex px-2 py-0.5 rounded bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 text-xs">Project</span>
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200" title={projectName(String(r.project_id))}>
                    {projectName(String(r.project_id))}
                  </span>
                  <select
                    value={r.role}
                    onChange={(e) => changeProjectLevel(r.id, e.target.value as AccessLevel)}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Editor">Editor</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeProjectRole(r.id)}
                    className="text-error hover:underline text-xs"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <hr className="border-gray-200 dark:border-gray-700 mb-4" />

        {/* ---- Grant / change access ---- */}
        <form onSubmit={handleGrant}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Change
            </label>
            <div className="space-y-2">
              {(['system', 'programme', 'project'] as GrantLevel[]).map((lvl) => (
                <label key={lvl} className="flex items-center">
                  <input
                    type="radio"
                    name="grantLevel"
                    value={lvl}
                    checked={grantLevel === lvl}
                    onChange={() => setGrantLevel(lvl)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{lvl} role</span>
                </label>
              ))}
            </div>
          </div>

          {grantLevel === 'system' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">System Role</label>
              <select value={systemRole} onChange={(e) => setSystemRole(e.target.value)} className={selectClasses}>
                {SYSTEM_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.value}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {SYSTEM_ROLE_OPTIONS.find((o) => o.value === systemRole)?.description || 'System roles apply across the entire application'}
              </p>
            </div>
          )}

          {grantLevel === 'programme' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Programme Role</label>
                <select value={programmeRole} onChange={(e) => setProgrammeRole(e.target.value as AccessLevel)} className={selectClasses}>
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Programme roles grant access to all projects within the selected programmes.
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Programmes{programmeIds.length > 0 ? ` (${programmeIds.length})` : ''}
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                  {programmes.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">No programmes available</div>
                  ) : (
                    programmes.map((programme) => {
                      const pid = String(programme.id);
                      const hasRole = existingProgrammeIds.includes(pid);
                      return (
                        <label
                          key={pid}
                          className={`flex items-center px-3 py-2 ${hasRole ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer'}`}
                          title={hasRole ? 'User already has a role in this programme' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={programmeIds.includes(pid)}
                            onChange={() => setProgrammeIds((prev) => toggleId(prev, pid))}
                            disabled={hasRole}
                            className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                            {programme.name}
                            {hasRole && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(already assigned)</span>}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}

          {grantLevel === 'project' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project Role</label>
                <select value={projectRole} onChange={(e) => setProjectRole(e.target.value as AccessLevel)} className={selectClasses}>
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This role will be assigned to all selected projects.</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Projects{projectIds.length > 0 ? ` (${projectIds.length})` : ''}
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                  {projects.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">No projects available</div>
                  ) : (
                    projects.map((project) => {
                      const pid = String(project.id);
                      const hasRole = existingProjectIds.includes(pid);
                      return (
                        <label
                          key={pid}
                          className={`flex items-center px-3 py-2 ${hasRole ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer'}`}
                          title={hasRole ? 'User already has a role in this project' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={projectIds.includes(pid)}
                            onChange={() => setProjectIds((prev) => toggleId(prev, pid))}
                            disabled={hasRole}
                            className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                            {project.name}
                            {hasRole && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(already assigned)</span>}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Done
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : grantLevel === 'system' ? 'Update Role' : 'Grant Access'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;
